import { useCreditNoteDetail, usePaymentDetail } from "@features/cx/payments/hooks";
import { CreditNote } from "@features/cx/payments/types";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@shared/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@shared/ui/dialog";
import { Separator } from "@shared/ui/separator";
import { FileText, User, DollarSign, Calendar, Printer, Download, Mail } from "lucide-react";

interface CreditNoteDetailDialogProps {
  open: boolean;
  onClose: () => void;
  creditNote?: CreditNote;
}

export function CreditNoteDetailDialog({ open, onClose, creditNote }: CreditNoteDetailDialogProps) {
  if (!creditNote) return null;
  const { data: creditNoteDetail } = useCreditNoteDetail({
    creditNoteId: creditNote.id,
    remoteUtilityId: creditNote.remoteUtilityId,
  });
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "active":
        return "secondary";
      case "applied":
        return "outline";
      case "expired":
        return "destructive";
      case "cancelled":
        return "destructive";
      default:
        return "secondary";
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Credit Note Details
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Header Information */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Credit Note {creditNoteDetail?.result?.receiptNo}</CardTitle>
                <Badge variant={getStatusBadgeVariant(creditNoteDetail?.result?.paymentReceivedStatus)}>
                  {creditNoteDetail?.result?.paymentReceivedStatus?.charAt(0).toUpperCase() + creditNoteDetail?.result?.paymentReceivedStatus?.slice(1)}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Consumer Information</span>
                  </div>
                  <div className="ml-6 space-y-1">
                    <p className="font-medium">{creditNoteDetail?.result?.consumer?.firstName} {creditNoteDetail?.result?.consumer?.lastName}</p>
                    <p className="text-sm text-muted-foreground">Account: {creditNoteDetail?.result?.consumer?.consumerNo}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Dates</span>
                  </div>
                  <div className="ml-6 space-y-1">
                    <p className="text-sm">Created: {creditNoteDetail?.result?.paymentDate}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Financial Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Financial Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Original Credit Amount</p>
                    <p className="text-2xl font-bold">${Number(creditNoteDetail?.result?.amount).toFixed(2)}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Payment Date</p>
                    <p className="text-lg font-medium text-blue-600">{creditNoteDetail?.result?.paymentDate}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Credit Note Information */}
          <Card>
            <CardHeader>
              <CardTitle>Credit Note Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Reason</p>
                  <Badge variant="outline">{creditNoteDetail?.result?.creditNoteReason}</Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Created By</p>
                  <p className="font-medium">{creditNoteDetail?.result?.createdUserRemoteName || "N/A"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Linked Payment</p>
                  <Button variant="link" className="p-0 h-auto">
                    {creditNoteDetail?.result?.referenceReceiptNo || "N/A"}
                  </Button>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge variant={getStatusBadgeVariant(creditNoteDetail?.result?.paymentReceivedStatus)}>
                    {creditNoteDetail?.result?.paymentReceivedStatus?.charAt(0).toUpperCase() + creditNoteDetail?.result?.paymentReceivedStatus?.slice(1)}
                  </Badge>
                </div>
              </div>
              
              {creditNoteDetail?.result?.remark && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Remarks</p>
                  <p className="text-sm bg-muted p-2 rounded">{creditNoteDetail?.result?.remark}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Separator />

          {/* Action Buttons */}
          <div className="flex justify-between">
            {/* <div className="flex gap-2">
              <Button variant="outline" onClick={handlePrint}>
                <Printer className="mr-2 h-4 w-4" />
                Print
              </Button>
              <Button variant="outline">
                <Download className="mr-2 h-4 w-4" />
                Download PDF
              </Button>
            </div> */}
            <Button onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
